// eslint-disable-next-line no-use-before-define
import React, { useState } from "react";
import { Popover } from "antd";
import { gql, useMutation } from "@apollo/client";
import dayjs from "dayjs";

import style from "./style.module.scss";

import { Arrow } from "../../components/Arrow";
import { FilePlusAlt } from "../../assets/filePlusAlt";
import { PasswordEye } from "../../assets/passwordEye";
import { Available } from "../../components/AvailableSpot";
import { NodeTransactionStatus, TransactionStatus } from "../../components/NodeTransactionStatus";
import { cashbackStatusLabels } from "../../utils/formatters";
import Link from "next/link";
import { useAuthApollo } from "@lib/apolloClientConfig";

interface ITransactionStatusWithAction {
	status: TransactionStatus;
	action: { [key: string]: ((e?: any) => void) | null };
	helper: typeof cashbackStatusLabels;
	message?: { [key: string]: string };
	showMessage: boolean;
	expiredOffer: boolean | undefined;
	spot?: number;
	loading: boolean;
	type: "default" | "visible";
	boletoExpirationDate?: Date;
	setWasScrolled: React.Dispatch<React.SetStateAction<boolean>>;
	wasScrolled: boolean;
	isMatriculae?: boolean;
	enrollmentStatus?: string | null;
	classId: string;
}

export const TransactionStatusWithAction = ({
	status,
	action,
	helper,
	message,
	showMessage,
	expiredOffer,
	spot,
	loading,
	type,
	boletoExpirationDate,
	setWasScrolled,
	wasScrolled,
	isMatriculae,
	enrollmentStatus,
	classId,
}: ITransactionStatusWithAction): JSX.Element => {
	const [visibleDeleteHiring, setVisibleDeletHiring] = useState(false);
	const client = useAuthApollo({ initialState: {} });

	React.useEffect(() => {
		if (!loading || wasScrolled) {
			setVisibleDeletHiring(false);
			setWasScrolled(false);
		}
	}, [loading, wasScrolled]);

	const menu = (
		<button onClick={() => action.handleMenu!(() => setVisibleDeletHiring(false))} className={style.dropdownCard}>
			{expiredOffer ? <span>Retirar da lista</span> : <span>Apagar contratação</span>}
		</button>
	);

	const boleto = (
		<button onClick={action.expired!} className={[type === "visible" && [style.btnContinue, style.blue].join(" "), style.dropdownCard].join(" ")}>
			<FilePlusAlt fill={type === "visible" ? "#FFFCFC" : "#807D7D"} />
			<span>{loading ? "Gerando..." : "Gerar novo boleto"}</span>
		</button>
	);

	const [redirectToMatriculae, { loading: loadingMatriculaeRedirect, error }] = useMutation(
		gql`
			mutation goToMatriculae($classId: String!) {
				redirectToMatriculae(classId: $classId) {
					success
					message
					redirectUrl
				}
			}
		`,
		{ client },
	);

	const handleGoToMatriculae = async () => {
		const { data: redirectToMatriculaeData } = await redirectToMatriculae({
			variables: { classId },
		});
		if (redirectToMatriculaeData?.redirectToMatriculae?.redirectUrl) {
			window.location.href = redirectToMatriculaeData.redirectToMatriculae.redirectUrl;
		}
	};

	const getStatus = () => {
		if (isMatriculae && enrollmentStatus === "PENDING") {
			return "pending";
		}

		return status!;
	};

	const paidStatus = () => {
		if (isMatriculae && enrollmentStatus === "PENDING") {
			return (
				<div className={style.actions}>
					<button className={`${[style.blue, style.btnContinue].join(" ")}`} onClick={handleGoToMatriculae}>
						<span>Concluir processo seletivo</span>
					</button>
				</div>
			);
		}

		return (
			<div className={style.actions}>
				<Link href="/minha-conta">
					<a className={[style.btn, style.gray].join(" ")}>
						<span>Ir para Minha conta</span>
						<Arrow className={style.arrow} />
					</a>
				</Link>
			</div>
		);
	};

	const typeButton = () => {
		switch (status) {
			case "paid" || "authorized":
				return paidStatus();

			case "expired":
				if (spot === 0) {
					return (
						<>
							<div className={style.actions}>
								<div className={style.btnPopover}>
									<Popover
										visible={visibleDeleteHiring}
										onVisibleChange={() => {
											setVisibleDeletHiring(!visibleDeleteHiring);
										}}
										content={menu}
										trigger="click"
										placement="bottomLeft"
									>
										<span className={style.suspensePoints}>...</span>
									</Popover>
									{expiredOffer ? (
										<button className={style.btnContinue} onClick={action.handleSearch!}>
											<span>Ver ofertas similares</span>
											<Arrow className={style.arrowContinue} />
										</button>
									) : (
										<button className={`${[style.blue, style.btnContinue].join(" ")}`} onClick={action.handleContinue!}>
											<span>Continuar</span>
											<Arrow className={style.arrowContinue} />
										</button>
									)}
								</div>
							</div>
						</>
					);
				} else {
					return (
						<div className={style.actions}>
							<button className={[style.btn, style.blue].join(" ")} onClick={action[status]!}>
								<FilePlusAlt fill="#FFFCFC" />
								<span>{loading ? "Gerando..." : "Gerar novo boleto"}</span>
							</button>
						</div>
					);
				}

			case "waiting_payment":
				return (
					<div className={style.actions}>
						<div className={[type === "visible" && style.responsive, style.btnPopover].join(" ")}>
							{type === "default" &&
								(dayjs().isAfter(dayjs(boletoExpirationDate)) || dayjs(boletoExpirationDate).isSame(dayjs(), "day")) && (
									<Popover
										visible={visibleDeleteHiring}
										onVisibleChange={() => {
											setVisibleDeletHiring(!visibleDeleteHiring);
										}}
										content={boleto}
										trigger="click"
										placement="bottomLeft"
									>
										<span className={style.suspensePoints}>...</span>
									</Popover>
								)}
							<button className={`${[style.blue, style.btnContinue].join(" ")}`} onClick={action.handleViewBoleto!}>
								<PasswordEye />
								<span>Visualizar boleto</span>
							</button>
							{type === "visible" &&
								(dayjs().isAfter(dayjs(boletoExpirationDate)) || dayjs(boletoExpirationDate).isSame(dayjs(), "day")) &&
								boleto}
						</div>
					</div>
				);

			default:
				return (
					<>
						<div className={`${[expiredOffer && style.opacity, style.warnings].join(" ")}`}>
							{spot && (
								<p className={style.spot}>
									<Available spot={spot} />
								</p>
							)}
						</div>
						<div className={style.actions}>
							<div className={style.btnPopover}>
								<Popover
									visible={visibleDeleteHiring}
									onVisibleChange={() => {
										setVisibleDeletHiring(!visibleDeleteHiring);
									}}
									content={menu}
									trigger="click"
									placement="bottomLeft"
								>
									<span className={style.suspensePoints}>...</span>
								</Popover>
								{isMatriculae && (
									<button className={`${[style.blue, style.btnContinue].join(" ")}`} onClick={action.handleContinue!}>
										<span>Continuar cadastro</span>
										<Arrow className={style.arrowContinue} />
									</button>
								)}
								{!isMatriculae && expiredOffer && (
									<button className={style.btnContinue} onClick={action.handleSearch!}>
										<span>Ver ofertas similares</span>
										<Arrow className={style.arrowContinue} />
									</button>
								)}
								{!isMatriculae && !expiredOffer && (
									<button className={`${[style.blue, style.btnContinue].join(" ")}`} onClick={action.handleContinue!}>
										<span>Continuar</span>
										<Arrow className={style.arrowContinue} />
									</button>
								)}
							</div>
						</div>
					</>
				);
		}
	};

	return (
		<>
			{!expiredOffer && <NodeTransactionStatus status={getStatus()} helper={helper} />}

			{showMessage && message && <span className={style.desc}>{message[getStatus()]}</span>}

			{typeButton()}
		</>
	);
};
